const GENERATION_MESSAGES = [
  'Wir tauchen den Pinsel in die Farbe …',
  'Die Leinwand wartet gespannt …',
  'Dein Tier wird zur Kunstikone …',
  'Wir mischen Umbra, Ocker und ein bisschen Magie …',
  'Die Schnurrhaare kriegen besondere Aufmerksamkeit …',
  'Etwas Glanz für das Fell, bitte …',
  'Wir stehlen ein paar Tricks von Rembrandt …',
  'Pinsel in Hand, Muse im Anflug …',
  'Das Porträt nimmt langsam Form an …',
  'Jeder Strich zählt – wir nehmen uns Zeit …',
  'Dein Haustier ahnt schon, dass etwas Besonderes entsteht …',
  'Die Leinwand atmet Farbe …',
  'Wir suchen den perfekten Ton für diese Augen …',
  'Ein Meisterwerk braucht manchmal einen Moment …',
  'Wir verleihen dem Fell Tiefe und Charakter …',
  'Der Rahmen wird schon poliert …',
  'Dein Tier sitzt Modell – (fast) geduldig …',
  'Wir setzen den letzten Glanzpunkt …',
  'Noch ein bisschen Weiß für die Highlights …',
  'Wir geben alles für ein porträtiertes Prachtexemplar …',
];

// Varianten mit Tiername (petName aus kunstpfote_uploads, assets/pet-name.js). Bewusst ohne
// Pronomen (er/sie/sein/ihr) – wir kennen das Geschlecht nicht. {name} = Name, {gen} = Genitiv.
const NAMED_MESSAGES = [
  '{name} sitzt Modell – (fast) geduldig …',
  'Wir suchen den perfekten Ton für {gen} Augen …',
  '{gen} Fell bekommt Tiefe und Charakter …',
  '{name} ahnt schon, dass etwas Besonderes entsteht …',
  'Noch ein bisschen Glanz für {gen} Fell …',
  'Wir setzen {name} ins rechte Licht …',
  'Die Leinwand wartet schon auf {name} …',
  '{name} wird gleich zur Kunstikone …',
  'Jeder Strich zählt, {name} ist es wert …',
];

/** Genitiv eines Namens: „Finas“, aber „Max’“ bei s, ß, x, z am Ende */
const genitive = (name) => (/[sßxz]$/i.test(name) ? `${name}’` : `${name}s`);

const NEWSLETTER_KEY = 'kunstpfote_newsletter_signed_up';

// Stufen wie im Prototyp, verteilt auf die Wartezeit (data-stage steuert die Foto-Filter im CSS)
const STAGES = [
  { at: 0, label: 'Dein Foto wird gelesen' },
  { at: 6_000, label: 'Skizze' },
  { at: 15_000, label: 'Farbe' },
  { at: 25_000, label: 'Feinschliff' },
];
const TITLE_WAITING = 'Dein Porträt entsteht';

const PROGRESS_DURATION = 35_000;
const POLL_INTERVAL = 1_000;
const MSG_INTERVAL = 3_800;

class GenerationProgressPanel extends HTMLElement {
  connectedCallback() {
    this._barFill = this.querySelector('.gen-modal__bar-fill');
    this._stepEl = this.querySelector('.gen-modal__step');
    this._photoImg = this.querySelector('.gen-modal__photo');
    this._laterBtn = this.querySelector('.gen-modal__later');
    this._barTrack = this.querySelector('.gen-modal__bar');
    this._messageEl = this.querySelector('.gen-modal__message');
    this._resultEl = this.querySelector('.gen-modal__result');
    this._resultImg = this.querySelector('.gen-modal__result-img');
    this._errorEl = this.querySelector('.gen-modal__error');
    this._ctaBtn = this.querySelector('.gen-modal__cta');
    this._titleEl = this.closest('dialog-component')?.querySelector('.gen-modal__title');

    this._ctaBtn?.addEventListener('click', () => this._goToOrder());
    this._laterBtn?.addEventListener('click', () => this.closest('dialog-component')?.closeDialog());

    this._newsletterEl = this.querySelector('.gen-modal__newsletter');
    this._setupNewsletter();

    // Clean up when the dialog closes
    this.closest('dialog-component')
      ?.querySelector('dialog[ref="dialog"]')
      ?.addEventListener('close', () => this._cleanup());

    window.addEventListener('kunstpfote:generation-started', (e) => {
      this._begin(e.detail);
    });
  }

  async _begin({ sessionId, sku, uploadBase, productUrl, name }) {
    // Während auf /generate gewartet wird, keinen zweiten Start zulassen (Doppelklick)
    if (this._starting) return;
    this._starting = true;
    this._done = false;
    this._sessionId = sessionId;
    this._sku = sku;
    this._uploadBase = uploadBase;
    this._productUrl = productUrl || '';
    this._imageUrl = `${uploadBase}/image/${sessionId}/generated/${sku}.jpg`;

    // Reset UI
    this.classList.remove('gen-modal__panel--done', 'gen-modal__panel--blocked', 'gen-modal__panel--reveal', 'gen-modal__panel--limit');
    const limitBox = this.querySelector('.gen-modal__limit');
    if (limitBox) limitBox.hidden = true;
    this.classList.add('gen-modal__panel--starting');
    this._setStage(0);
    this._petName = this._readPetName();
    if (this._titleEl) {
      this._titleEl.textContent = this._petName ? `${genitive(this._petName)} Porträt entsteht` : TITLE_WAITING;
    }
    if (this._ctaBtn) this._ctaBtn.hidden = true;
    if (this._newsletterEl) this._newsletterEl.hidden = this._newsletterDone();
    this._resultEl.hidden = true;
    // removeAttribute statt src = '' – ein leeres src lädt in manchen Browsern die aktuelle Seite
    this._resultImg.removeAttribute('src');
    this._showOwnPhoto(sessionId, uploadBase);
    // Motivname nur auf Produktseiten (blocks/_product-details.liquid)
    this._motivName = document.querySelector('[data-motiv-name]')?.dataset.motivName || '';
    this._errorEl.hidden = true;
    this._barFill.style.transition = '';
    this._setProgress(0);
    this._messageEl.classList.remove('gen-modal__message--visible');

    // Open the dialog, then lock it
    this.closest('dialog-component')?.showDialog();
    this._lockModal();

    // Call /generate and wait briefly for the answer: a blocked session (403) answers
    // instantly and must never see the progress bar. Max. 1.5 s, then start regardless.
    const call = this._callGenerate(sessionId, sku, uploadBase, name);
    await Promise.race([call, new Promise((r) => setTimeout(r, 1_500))]);
    this._starting = false;
    this.classList.remove('gen-modal__panel--starting');
    if (this._done) return; // gesperrt (403) oder Modal inzwischen geschlossen

    // Fake progress: 0 → 90% over 35 s
    this._startProgress();

    // Rotating witty messages
    this._startMessages();

    // Poll every second for the generated image
    this._startPolling();
  }

  async _callGenerate(sessionId, sku, uploadBase, name) {
    try {
      const body = { sessionId };
      if (sku) body.motiv = sku;
      if (name) body.name = name;
      const petName = this._readPetName();
      if (petName) body.petName = petName;

      const res = await fetch(`${uploadBase}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        if (res.status === 429 && json.code === 'limit_reached') {
          this._showLimit(json);
          return;
        }
        const msg = res.status === 503
          ? 'Server gerade beschäftigt – wir versuchen es weiter …'
          : json.error || `Fehler beim Starten (${res.status})`;
        if (!this._done) {
          this._errorEl.textContent = msg;
          this._errorEl.hidden = false;
        }
        // 403 = Session im Admin gesperrt, 422 = kein Foto hat die Prüfung bestanden
        // (KI/Grafik, Screenshot, Person, kein Tier) → nicht weiter pollen, Modal freigeben
        // 429 + limit_reached = Tageslimit an Vorschauen erreicht
        if (res.status === 403 || res.status === 422 || (res.status === 429 && json.code === 'limit_reached')) {
          this.classList.add('gen-modal__panel--blocked');
          this._cleanup();
        }
      }
    } catch {
      // Network error – polling continues, server may have queued the job
    }
  }

  /** Tageslimit erreicht: statt Fortschritt die heutigen Vorschauen zeigen, je mit Link zur Produktseite */
  _showLimit(json) {
    const box = this.querySelector('.gen-modal__limit');
    if (!box) return;
    // Produktseiten der bereits erzeugten Motive (gemerkt beim Fertigstellen, siehe _onImageReady)
    let known = [];
    try { known = JSON.parse(localStorage.getItem('kunstpfote_uploads') || 'null')?.generated || []; } catch {}
    const urlFor = (sku) => {
      const hit = known.find((e) => (typeof e === 'string' ? e.replace(/\.jpg$/i, '') : e?.sku) === sku);
      return (hit && typeof hit === 'object' && hit.url) || `/search?q=${encodeURIComponent(sku)}`;
    };
    const items = Array.isArray(json.items) ? json.items : [];
    const limit = json.limit || items.length;
    if (this._titleEl) this._titleEl.textContent = `Deine ${limit} Vorschauen für heute`;
    box.querySelector('.gen-modal__limit-text').textContent =
      'Du hast heute alle Vorschauen genutzt. Morgen kannst du wieder neue ausprobieren. ' +
      (items.length ? 'Hier sind deine Porträts, such dir dein Lieblingsmotiv aus:' : 'Wenn es eilt, schreib mir gern an hallo@kunstpfote.de.');
    const list = box.querySelector('.gen-modal__limit-list');
    list.textContent = '';
    for (const it of items) {
      const li = document.createElement('li');
      li.className = 'gen-modal__limit-item';
      const img = document.createElement('img');
      img.alt = 'Deine Vorschau';
      img.loading = 'lazy';
      img.src = `${this._uploadBase}/image/${this._sessionId}/generated/${encodeURIComponent(it.sku)}.jpg?v=${Date.now()}`;
      const a = document.createElement('a');
      a.className = 'button';
      a.href = urlFor(it.sku);
      a.textContent = 'Zum Porträt';
      li.append(img, a);
      list.append(li);
    }
    box.hidden = false;
    this._errorEl.hidden = true;
    this.classList.add('gen-modal__panel--limit');
    this._cleanup();
  }

  /** Eigenes Foto der Session auf der Bühne (erstes hochgeladenes Original) */
  _showOwnPhoto(sessionId, uploadBase) {
    if (!this._photoImg) return;
    let originals = [];
    try {
      originals = JSON.parse(localStorage.getItem('kunstpfote_uploads') || 'null')?.originals || [];
    } catch {}
    this._photoImg.removeAttribute('src');
    this._photoImg.hidden = true;
    if (!originals.length) return;
    const show = (file) => {
      if (!file || this._sessionId !== sessionId) return;
      this._photoImg.src = `${uploadBase}/image/${sessionId}/originals/${file}`;
      this._photoImg.hidden = false;
    };
    // Erstes Foto, das wirklich in die Generierung geht (Foto-Prüfung, GET /check) – nicht einfach das erste
    fetch(`${uploadBase}/check/${sessionId}`, { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((check) => {
        const used = originals.filter((f) => {
          const p = check?.photos?.[f];
          return !p || (p.used ?? p.use) !== false;
        });
        show(used[0] || null);
      })
      .catch(() => show(originals[0]));
  }

  _setStage(index) {
    this.dataset.stage = String(index);
    const stage = STAGES[index];
    if (stage && this._stepEl) this._stepEl.textContent = stage.label;
  }

  _startProgress() {
    const t0 = performance.now();
    const tick = () => {
      if (this._done) return;
      const elapsed = performance.now() - t0;
      const pct = Math.min((elapsed / PROGRESS_DURATION) * 90, 90);
      this._setProgress(pct);
      const stage = STAGES.findLastIndex((s) => elapsed >= s.at);
      if (String(stage) !== this.dataset.stage) this._setStage(stage);
      if (pct < 90) this._rafId = requestAnimationFrame(tick);
    };
    this._rafId = requestAnimationFrame(tick);
  }

  /** Tiername aus der Session (assets/pet-name.js), sonst '' */
  _readPetName() {
    // Korrekte Schreibweise über assets/pet-name.js; Rückfall: gespeicherter Wert
    if (window.KunstPfotePetName) return window.KunstPfotePetName.get();
    try {
      const name = JSON.parse(localStorage.getItem('kunstpfote_uploads') || 'null')?.petName;
      return typeof name === 'string' ? name.trim() : '';
    } catch {
      return '';
    }
  }

  _startMessages() {
    const shuffle = (list) => [...list].sort(() => Math.random() - 0.5);
    let msgs = shuffle(GENERATION_MESSAGES);

    // Mit Tiername: Namens-Varianten untermischen, der erste Spruch nennt immer den Namen
    if (this._petName) {
      const named = shuffle(NAMED_MESSAGES).map((m) =>
        m.replaceAll('{gen}', genitive(this._petName)).replaceAll('{name}', this._petName)
      );
      msgs = [];
      const generic = shuffle(GENERATION_MESSAGES);
      while (named.length || generic.length) {
        if (named.length) msgs.push(named.shift());
        if (generic.length) msgs.push(generic.shift());
      }
    }
    let idx = 0;

    const show = () => {
      if (this._done) return;
      this._messageEl.classList.remove('gen-modal__message--visible');
      setTimeout(() => {
        if (this._done) return;
        this._messageEl.textContent = msgs[idx % msgs.length];
        this._messageEl.classList.add('gen-modal__message--visible');
        idx++;
      }, 350);
    };

    show();
    this._msgTimer = setInterval(show, MSG_INTERVAL);
  }

  _startPolling() {
    this._pollTimer = setInterval(() => {
      if (this._done) return;
      const img = new Image();
      img.onload = () => { if (!this._done) this._onImageReady(); };
      img.src = `${this._imageUrl}?_t=${Date.now()}`;
    }, POLL_INTERVAL);
  }

  _lockModal() {
    const dc = this.closest('dialog-component');
    this._closeBtn = dc?.querySelector('.gen-modal__close');
    this._lockedDialog = dc?.querySelector('dialog[ref="dialog"]');

    if (this._closeBtn) this._closeBtn.hidden = true;

    this._escapeGuard = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    this._lockedDialog?.addEventListener('keydown', this._escapeGuard, { capture: true });
  }

  _unlockModal() {
    if (this._closeBtn) this._closeBtn.hidden = false;
    if (this._escapeGuard && this._lockedDialog) {
      this._lockedDialog.removeEventListener('keydown', this._escapeGuard, { capture: true });
    }
    this._closeBtn = null;
    this._escapeGuard = null;
    this._lockedDialog = null;
  }

  async _showQuota() {
    const el = this.querySelector('.gen-modal__quota');
    if (!el || !this._sessionId || !this._uploadBase) return;
    try {
      const res = await fetch(`${this._uploadBase}/check/${this._sessionId}`, { cache: 'no-store' });
      const g = (await res.json()).generations;
      if (!g) return;
      el.textContent = g.left > 0
        ? `Du kannst heute noch ${g.left} von ${g.limit} Vorschauen erstellen.`
        : `Das war deine letzte Vorschau für heute (${g.limit} pro Tag). Morgen kannst du wieder neue ausprobieren.`;
      el.hidden = false;
    } catch {}
  }

  _onImageReady() {
    this._done = true;
    clearInterval(this._pollTimer);
    clearInterval(this._msgTimer);
    cancelAnimationFrame(this._rafId);
    this._unlockModal();

    // Persist SKU in localStorage (guard against missing SKU)
    const sku = this._sku;
    if (sku) {
      const UPLOAD_DATA_KEY = 'kunstpfote_uploads';
      try {
        const raw = localStorage.getItem(UPLOAD_DATA_KEY);
        if (raw) {
          const data = JSON.parse(raw);
          data.generated = data.generated ?? [];
          const alreadyStored = data.generated.some((e) => {
            const s = typeof e === 'string' ? e.replace(/\.jpg$/i, '') : e?.sku;
            return s === sku;
          });
          if (!alreadyStored) {
            const entry = { sku };
            if (this._productUrl) entry.url = this._productUrl;
            data.generated.push(entry);
          }
          localStorage.setItem(UPLOAD_DATA_KEY, JSON.stringify(data));
        }
      } catch {}
    }

    // Notify PDP block and nav modal
    window.dispatchEvent(new CustomEvent('kunstpfote:generation-complete', {
      detail: { sku, sessionId: this._sessionId, uploadBase: this._uploadBase },
    }));

    // Wie viele Vorschauen heute noch möglich sind (Tageslimit, Server: MAX_GENERATIONS)
    this._showQuota();

    // Reveal image – progress bar and message give way to the result
    this._resultImg.onload = () => {
      this.classList.add('gen-modal__panel--done');
      this._setProgress(100);
      // Foto blendet ins Porträt über, danach wächst der Rahmen, Lichtreflex + Goldfunken (CSS)
      this.dataset.stage = '4';
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('gen-modal__panel--reveal')));
      if (this._titleEl) {
        // Wie der PDP-Titel: „Fina in den Bergen“ (Wendung pro Motiv, pet-name.js);
        // ohne Motiv „Finas Porträt ist fertig“
        const name = this._petName;
        const helper = window.KunstPfotePetName;
        const handle = helper?.handleFromPath(this._productUrl) || '';
        this._titleEl.textContent =
          name && this._motivName
            ? (helper ? helper.phrase(name, handle, this._motivName) : `${name}, ${this._motivName}`)
          : name ? `${genitive(name)} Porträt ist fertig`
          : this._motivName || 'Dein Porträt ist fertig';
      }
      // CTA only where there is an order section to jump to (PDP)
      if (this._ctaBtn) this._ctaBtn.hidden = !document.querySelector('.pf-order');
      this._resultEl.hidden = false;
    };
    this._resultImg.src = `${this._imageUrl}?v=${Date.now()}`;
  }

  _newsletterDone() {
    try {
      return localStorage.getItem(NEWSLETTER_KEY) === '1';
    } catch {
      return false;
    }
  }

  // The form posts into a hidden iframe instead of the page: a normal submit would reload the
  // page and abort the generation, and fetch() can't pass Shopify's hCaptcha (its script
  // intercepts the submit, adds a token and submits natively – which then lands in the iframe).
  // Shopify's native double opt-in stays in charge. Any page landing in the iframe counts as sent,
  // only a captcha challenge counts as failure – the email format is already checked by the browser.
  _setupNewsletter() {
    const form = this._newsletterEl?.querySelector('form');
    const frame = this._newsletterEl?.querySelector('iframe');
    if (!form || !frame) return;

    form.target = frame.name;
    const button = form.querySelector('button[type="submit"]');
    const errorEl = form.querySelector('.gen-modal__newsletter-error');
    let submitted = false;
    let timeout;

    const fail = () => {
      clearTimeout(timeout);
      errorEl.textContent = 'Das hat leider nicht geklappt – bitte prüf deine E-Mail-Adresse und versuch es nochmal.';
      errorEl.hidden = false;
      button.disabled = false;
    };

    form.addEventListener('submit', () => {
      submitted = true;
      errorEl.hidden = true;
      button.disabled = true;
      clearTimeout(timeout);
      timeout = setTimeout(fail, 20_000);
    });

    frame.addEventListener('load', () => {
      if (!submitted) return;
      let url = '';
      try {
        url = frame.contentWindow.location.href;
      } catch {
        // Unreadable (e.g. cross-origin redirect) – the post went through
      }
      if (url === 'about:blank') return;
      if (url.includes('/challenge')) return fail();

      clearTimeout(timeout);
      errorEl.hidden = true;
      try {
        localStorage.setItem(NEWSLETTER_KEY, '1');
      } catch {}
      form.hidden = true;
      this._newsletterEl.querySelector('.gen-modal__newsletter-success').hidden = false;
    });
  }

  async _goToOrder() {
    // Scroll only after close – closing restores the scroll position locked by the dialog
    await this.closest('dialog-component')?.closeDialog();
    // Title + price, not the order section itself – on mobile price and ATC button still fit on screen
    document.querySelector('.product-title-price')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _setProgress(pct) {
    const v = Math.round(pct);
    this._barFill.style.width = `${pct}%`;
    this._barTrack?.setAttribute('aria-valuenow', v);
  }

  _cleanup() {
    this._done = true;
    clearInterval(this._pollTimer);
    clearInterval(this._msgTimer);
    cancelAnimationFrame(this._rafId);
    this._unlockModal();
  }
}

customElements.define('generation-progress-panel', GenerationProgressPanel);
